import { 
  ContentGenerationInput, 
  ContentGenerationOutput,
  GenerationProgress,
  ArticleOutline
} from './types';
import { generateOutline } from './1-outline-generator';
import { generateAllSections } from './2-section-writer';
import { assembleArticle } from './3-article-assembler';
import { polishArticle } from './4-content-polisher';
import { buildOutlinePrompt, buildSectionPrompt, buildAssemblyPrompt, buildPolishPrompt } from './prompts';
import { formatExtractedDataForPrompt, ExtractedPageData } from '../url-content-extractor';
import { GenerationCost } from '../openai-chat';

export type PromptApprovalCallback = (
  stageName: string,
  stageNumber: number,
  defaultPrompt: string
) => Promise<string>;

export type OutlineApprovalCallback = (
  outline: ArticleOutline
) => Promise<ArticleOutline>;

export type PartialResultCallback = (
  html: string,
  stage: number,
  message: string
) => void;

export type CostUpdateCallback = (
  stageName: string,
  stageCost: GenerationCost,
  totalCost: GenerationCost
) => void;

interface InteractiveGenerationInput extends Omit<ContentGenerationInput, 'onProgress' | 'extractedData'> {
  onProgress?: (progress: GenerationProgress) => void;
  onPromptRequest?: PromptApprovalCallback;
  onOutlineRequest?: OutlineApprovalCallback;
  onPartialResult?: PartialResultCallback;
  onCostUpdate?: CostUpdateCallback;
  humanizeContent?: boolean;
  // Додаткові параметри для розумної генерації
  language?: string;
  contentType?: string;
  intent?: string;
  extractedData?: ExtractedPageData; // Дані витягнуті з URL (об'єкт)
}

/**
 * Interactive orchestrator that pauses for prompt approval at each stage
 */
export async function generateArticleInteractive(
  input: InteractiveGenerationInput
): Promise<ContentGenerationOutput> {
  const { 
    competitors, 
    keywords, 
    projectContext, 
    customPrompt, 
    onProgress, 
    onPromptRequest, 
    onOutlineRequest, 
    onPartialResult, 
    onCostUpdate,
    humanizeContent = false,
    language,
    contentType,
    intent,
    extractedData
  } = input;

  // Accumulate total cost
  let totalCost: GenerationCost = {
    inputTokens: 0,
    outputTokens: 0,
    inputCost: 0,
    outputCost: 0,
    totalCost: 0,
    model: 'gpt-4.1'
  };

  try {
    console.log('🚀 [Interactive Orchestrator] Starting content generation');
    console.log('📊 [Interactive Orchestrator] Content settings:', { language, contentType, intent });
    
    // Форматуємо extractedData якщо є
    const formattedExtractedData = extractedData ? formatExtractedDataForPrompt(extractedData) : undefined;
    console.log('🔍 [Interactive Orchestrator] Formatted extracted data:', formattedExtractedData);
    
    // ============ STAGE 1: OUTLINE ============
    onProgress?.({
      stage: 1,
      totalStages: 4,
      message: 'Preparing outline generation...',
      progress: 5
    });

    const outlinePrompt = buildOutlinePrompt(
      competitors, 
      keywords, 
      projectContext,
      language,
      contentType,
      intent,
      formattedExtractedData
    );
    
    console.log('📝 [STAGE 1 - OUTLINE] Default prompt prepared');
    
    // Request user approval/edit
    const approvedOutlinePrompt = onPromptRequest
      ? await onPromptRequest('Create Article Outline', 1, outlinePrompt)
      : outlinePrompt;

    onProgress?.({
      stage: 1,
      totalStages: 4,
      message: 'Generating article outline...',
      progress: 10
    });

    const { outline: generatedOutline, cost: outlineCost } = await generateOutline(
      competitors, 
      keywords, 
      projectContext, 
      language, 
      contentType, 
      intent, 
      formattedExtractedData, 
      approvedOutlinePrompt
    );
    
    totalCost.inputTokens += outlineCost.inputTokens;
    totalCost.outputTokens += outlineCost.outputTokens;
    totalCost.inputCost += outlineCost.inputCost;
    totalCost.outputCost += outlineCost.outputCost;
    totalCost.totalCost += outlineCost.totalCost;
    
    onCostUpdate?.('Outline', outlineCost, { ...totalCost });

    let outline = generatedOutline;

    // Request outline approval/edit
    if (onOutlineRequest) {
      console.log('📝 [STAGE 1 - OUTLINE] Requesting outline approval...');
      console.log('📝 [STAGE 1 - OUTLINE] Generated outline has FAQ?', !!outline.faq);
      console.log('📝 [STAGE 1 - OUTLINE] FAQ content:', outline.faq);
      console.log('📝 [STAGE 1 - OUTLINE] includeFaq flag:', outline.includeFaq);
      outline = await onOutlineRequest(outline);
      console.log('📝 [STAGE 1 - OUTLINE] Outline approved/edited');
      console.log('📝 [STAGE 1 - OUTLINE] Approved outline sections:', outline.sections.map(s => ({ h2: s.h2, h3: s.h3 })));
    }

    onProgress?.({
      stage: 1,
      totalStages: 4,
      message: `Outline created: ${outline.sections.length} sections planned`,
      progress: 25
    });

    // ============ STAGE 2: SECTIONS ============
    const allSections = [
      ...(outline.includeIntroduction !== false && outline.introduction ? [outline.introduction] : []),
      ...outline.sections,
      ...(outline.includeConclusion !== false && outline.conclusion ? [outline.conclusion] : []),
      ...(outline.includeCta !== false && outline.cta ? [outline.cta] : [])
      // FAQ генеруємо окремо ПІСЛЯ Stage 4
    ];
    
    console.log('📝 [STAGE 2 - SECTIONS] Total sections to generate:', allSections.length);
    console.log('📝 [STAGE 2 - SECTIONS] Section titles:', allSections.map(s => s.h2));
    console.log('📝 [STAGE 2 - SECTIONS] FAQ will be generated separately after deduplication');
    
    const totalSections = allSections.length;
    const sections = [];

    for (let i = 0; i < allSections.length; i++) {
      const section = allSections[i];
      
      console.log(`\n🔍 [SECTION ${i + 1}/${totalSections}] Processing section:`, {
        h2: section.h2,
        h3Count: section.h3?.length || 0,
        h3List: section.h3 || [],
        keywords: section.targetKeywords
      });
      
      onProgress?.({
        stage: 2,
        totalStages: 4,
        message: `Preparing section ${i + 1}/${totalSections}: "${section.h2}"`,
        progress: 25 + ((i / totalSections) * 35),
        currentSection: i + 1,
        totalSections
      });

      // Find relevant competitor content
      const competitorExamples: string[] = [];
      for (const comp of competitors.slice(0, 3)) {
        if (comp.text) {
          const paragraphs = comp.text.split('\n').filter(p => p.length > 100);
          const relevant = paragraphs.find(p => 
            p.toLowerCase().includes(section.h2.toLowerCase().split(' ')[0])
          );
          if (relevant) competitorExamples.push(relevant);
        }
      }

      const sectionPrompt = buildSectionPrompt(
        section.h2,
        section.h3 || [],
        section.targetKeywords,
        projectContext,
        competitorExamples,
        sections.map(s => ({ h2: s.h2, html: s.html })), // Попередні секції
        totalSections,
        i,
        section.estimatedWordCount || 600, // Цільова к-сть слів
        humanizeContent, // Передаємо опцію гуманізації
        language,
        contentType,
        intent,
        formattedExtractedData
      );

      console.log(`📝 [STAGE 2 - SECTION ${i + 1}] Default prompt prepared`);
      console.log(`📝 [STAGE 2 - SECTION ${i + 1}] Prompt first 500 chars:`, sectionPrompt.substring(0, 500));

      // Request user approval/edit
      const approvedSectionPrompt = onPromptRequest
        ? await onPromptRequest(
            `Write Section: ${section.h2}`,
            2,
            sectionPrompt
          )
        : sectionPrompt;

      console.log(`📝 [STAGE 2 - SECTION ${i + 1}] Prompt approved/edited`);
      console.log(`📝 [STAGE 2 - SECTION ${i + 1}] Final prompt first 500 chars:`, approvedSectionPrompt.substring(0, 500));

      onProgress?.({
        stage: 2,
        totalStages: 4,
        message: `Writing section ${i + 1}/${totalSections}: "${section.h2}"`,
        progress: 25 + (((i + 0.5) / totalSections) * 35),
        currentSection: i + 1,
        totalSections
      });

      // Generate section with approved prompt
      const { generateSection } = await import('./2-section-writer');
      const { section: sectionResult, cost: sectionCost } = await generateSection(
        section,
        i,
        projectContext,
        competitors,
        sections, // Попередні секції
        totalSections,
        approvedSectionPrompt,
        humanizeContent,
        language,
        contentType,
        intent,
        formattedExtractedData
      );
      
      // Update total cost
      totalCost.inputTokens += sectionCost.inputTokens;
      totalCost.outputTokens += sectionCost.outputTokens;
      totalCost.inputCost += sectionCost.inputCost;
      totalCost.outputCost += sectionCost.outputCost;
      totalCost.totalCost += sectionCost.totalCost;
      
      onCostUpdate?.(`Section ${i + 1}`, sectionCost, { ...totalCost });
      
      sections.push(sectionResult);
      
      console.log(`📝 Section ${i + 1} HTML (first 300 chars):`, sectionResult.html.substring(0, 300));
      console.log(`📝 Section ${i + 1} contains HTML tags:`, /<[^>]+>/.test(sectionResult.html));
      
      // Оновлюємо часткові результати - показуємо вже згенеровані секції
      if (onPartialResult) {
        const partialHTML = sections.map(s => {
          const h2Tag = s.h2 ? `<h2>${s.h2}</h2>\n` : '';
          return `${h2Tag}${s.html}`;
        }).join('\n\n');
        
        console.log(`📄 Partial HTML (first 300 chars):`, partialHTML.substring(0, 300));
        
        onPartialResult(
          partialHTML,
          2,
          `Section ${i + 1}/${totalSections} completed`
        );
      }
    }

    onProgress?.({
      stage: 2,
      totalStages: 4,
      message: `All ${totalSections} sections completed`,
      progress: 60
    });

    // ============ STAGE 3: ASSEMBLE (просто склеюємо) ============
    onProgress?.({
      stage: 3,
      totalStages: 4,
      message: 'Assembling final article...',
      progress: 70
    });

    console.log('📝 [STAGE 3 - ASSEMBLY] Joining all sections');
    
    // Просто збираємо всі секції разом
    const finalHTML = `<h1>${outline.h1}</h1>\n\n` + 
      sections.map(s => {
        const h2Tag = s.h2 ? `<h2>${s.h2}</h2>\n` : '';
        return `${h2Tag}${s.html}`;
      }).join('\n\n');
    
    console.log('📝 [STAGE 3 - ASSEMBLY] Final HTML length:', finalHTML.length);
    console.log('📝 [STAGE 3 - ASSEMBLY] First 500 chars:', finalHTML.substring(0, 500));
    console.log('📝 [STAGE 3 - ASSEMBLY] Contains HTML tags:', /<[^>]+>/.test(finalHTML));
    
    const textContent = finalHTML.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ');
    const totalWordCount = textContent.split(' ').filter(w => w.length > 0).length;

    onProgress?.({
      stage: 3,
      totalStages: 4,
      message: `Article assembled: ${totalWordCount} words`,
      progress: 80
    });

    // Показуємо фінальний результат
    if (onPartialResult) {
      onPartialResult(
        finalHTML,
        3,
        'Article fully assembled'
      );
    }

    // ============ STAGE 4: SKIP (Quality controlled during generation) ============
    console.log('📝 [STAGE 4 - SKIP] Quality and uniqueness checked during section generation');
    
    onProgress?.({
      stage: 4,
      totalStages: 4,
      message: 'Applying final formatting...',
      progress: 85
    });
    
    // Технічні правки: тільки заміна тире, без AI
    let polishedHTML = finalHTML
      .replace(/—/g, '–')  // em dash → en dash
      .replace(/―/g, '–')  // horizontal bar → en dash
      .replace(/\u2014/g, '\u2013')  // em dash (Unicode) → en dash
      .replace(/\u2015/g, '\u2013'); // horizontal bar (Unicode) → en dash
    
    console.log('📝 [STAGE 4 - TECHNICAL] Em dashes replaced');
    
    // Показуємо фінальний результат
    if (onPartialResult) {
      onPartialResult(
        polishedHTML,
        4,
        'Article formatting completed'
      );
    }

    // ============ GENERATE FAQ SEPARATELY ============
    let finalHTMLWithFaq = polishedHTML;
    
    if (outline.includeFaq === true && outline.faq) {
      console.log('📝 [FAQ GENERATION] Generating FAQ section separately...');
      
      onProgress?.({
        stage: 4,
        totalStages: 4,
        message: 'Generating FAQ section...',
        progress: 92
      });
      
      const { generateSection } = await import('./2-section-writer');
      const { section: faqSection, cost: faqCost } = await generateSection(
        outline.faq,
        sections.length, // Index after all other sections
        projectContext,
        competitors,
        sections, // All previously generated sections
        sections.length + 1, // Total including FAQ
        undefined, // Use default prompt
        humanizeContent,
        language,
        contentType,
        intent,
        formattedExtractedData
      );
      
      // Update total cost
      totalCost.inputTokens += faqCost.inputTokens;
      totalCost.outputTokens += faqCost.outputTokens;
      totalCost.inputCost += faqCost.inputCost;
      totalCost.outputCost += faqCost.outputCost;
      totalCost.totalCost += faqCost.totalCost;
      
      onCostUpdate?.('FAQ Section', faqCost, { ...totalCost });
      
      // Add FAQ to the end
      const faqH2 = faqSection.h2 ? `<h2>${faqSection.h2}</h2>\n` : '';
      const faqHTML = `${faqH2}${faqSection.html}`;
      finalHTMLWithFaq = polishedHTML + '\n\n' + faqHTML;
      
      sections.push(faqSection);
      
      console.log('📝 [FAQ GENERATION] FAQ added to the end (length:', faqHTML.length, ')');
      
      // Show final result with FAQ
      if (onPartialResult) {
        onPartialResult(
          finalHTMLWithFaq,
          4,
          'FAQ section added'
        );
      }
    }

    // Встановлюємо прогрес на 100% після всіх секцій включно з FAQ
    onProgress?.({
      stage: 4,
      totalStages: 4,
      message: 'Content generation complete!',
      progress: 100
    });

    console.log('🎉 [Interactive Orchestrator] All stages completed successfully!');
    
    // Перераховуємо word count для фінальної версії (з FAQ якщо є)
    const finalTextContent = finalHTMLWithFaq.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ');
    const finalWordCount = finalTextContent.split(' ').filter(w => w.length > 0).length;
    
    return {
      outline,
      sections,
      finalArticle: finalHTMLWithFaq,
      wordCount: finalWordCount
    };

  } catch (error) {
    console.error('❌ [Interactive Orchestrator] Error:', error);
    throw error;
  }
}
